// The edits live per plan in localStorage and the geometry they apply to lives
// in the agent's per-plan view bundle, so an export that covers the project has
// to gather both for every plan — not just the one the estimator has open.

import { fetchProjectAsset } from "components/SmartSurveyor/SmartSurveyorV3/utils/signedAssetUrls";
import { fetchCableTakeoffPaths } from "../../utils/fetchCableTakeoffPaths";
import {
  DEFAULT_PX_PER_M,
  migrateStoredOverrides,
  type CableEditOverrides,
} from "../../CableTakeoffTabV3/cableEditGeometry";
import { cableEditStorageKey } from "../../CableTakeoffTabV3/useEditableCableTakeoff";
import { loadRouteCache, routeCacheStorageKey } from "../../CableTakeoffTabV3/routeCache";
import type { CableViewBundle } from "../../CableTakeoffTabV3/findCableGaps";
import type { CableExportPlan } from "../buildCableExportRows";
import type { LoadCableExportPlans } from "./types";

const viewBundleKey = (
  projectId: string,
  annotationKey: string,
): string | null => {
  const annotationDir = annotationKey
    .split("/annotations/")[1]
    ?.split("/counts.json")[0];
  if (!annotationDir) return null;
  return `smart-surveyor/projects/${projectId}/agent_output/cables/view/${annotationDir}.json`;
};

const readOverrides = (
  projectId: string,
  annotationKey: string | null | undefined,
): CableEditOverrides => {
  if (!annotationKey) return {};
  try {
    const raw = window.localStorage.getItem(
      cableEditStorageKey(projectId, annotationKey),
    );
    return raw ? migrateStoredOverrides(JSON.parse(raw) as CableEditOverrides) : {};
  } catch {
    return {};
  }
};

const fetchViewBundle = async (
  projectId: string,
  key: string,
): Promise<CableViewBundle | null> => {
  try {
    const response = await fetchProjectAsset({ projectId, key });
    if (!response.ok) return null;
    return (await response.json()) as CableViewBundle;
  } catch {
    return null;
  }
};

export const loadCableExportPlans: LoadCableExportPlans = async ({
  projectId,
  cables,
}) => {
  const plans = cables?.plans ?? [];
  if (plans.length === 0) return [];

  return Promise.all(
    plans.map(async (plan): Promise<CableExportPlan> => {
      const annotationKey = plan.annotation_key;
      const overrides = readOverrides(projectId, annotationKey);
      const key = annotationKey
        ? viewBundleKey(projectId, annotationKey)
        : null;
      const bundle = key ? await fetchViewBundle(projectId, key) : null;
      // The run's own scale, published beside the geometry it measured — the
      // export has no plan on screen to take a calibrated page scale from.
      const imageScaleFactor = bundle?.px_per_pt || 1;
      const scalePxPerM = bundle?.scale || DEFAULT_PX_PER_M;
      // Same session-stable signed URL the bundle came from, so this is served
      // from the browser cache rather than a second trip to S3.
      const geometry = annotationKey
        ? await fetchCableTakeoffPaths(
            projectId,
            annotationKey,
            imageScaleFactor,
          )
        : null;

      return {
        planName: plan.plan_name,
        items: plan.boq_items ?? [],
        geometry,
        overrides,
        imageScaleFactor,
        scalePxPerM,
        routes: loadRouteCache(
          annotationKey ? routeCacheStorageKey(projectId, annotationKey) : null,
        ),
      };
    }),
  );
};
